import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  clearPendingCheckout,
  extractCheckoutPayload,
  isFailedInvestmentStatus,
  isPaidInvestmentStatus,
  readPendingCheckout,
  syncInvestmentSession,
  getStripeSessionId,
  INVESTMENT_PAYMENT_ROUTES,
} from "lib/investmentHelper";

/**
 * Confirms a Stripe Checkout session on the success return page
 * via POST /api/investments/sync-session.
 */
export function useConfirmInvestmentPayment({ redirectOnFailure = true } = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading" });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const sessionId =
      getStripeSessionId(searchParams) || readPendingCheckout()?.sessionId;
    const pending = readPendingCheckout();

    if (!sessionId) {
      setState({
        phase: "error",
        message:
          "We could not find your checkout session. If you completed payment, check your dashboard or contact support.",
        projectId: pending?.projectId ?? null,
        amount: pending?.amount ?? null,
      });
      return;
    }

    (async () => {
      try {
        const resp = await syncInvestmentSession(sessionId);
        const payload = extractCheckoutPayload(resp);
        clearPendingCheckout();

        const projectId =
          pending?.projectId ??
          payload.investment?.project?._id ??
          payload.investment?.project?.id ??
          payload.investment?.projectId ??
          null;
        const amount = pending?.amount ?? payload.investment?.amount ?? null;

        if (isFailedInvestmentStatus(payload.status)) {
          if (redirectOnFailure) {
            navigate(
              `${INVESTMENT_PAYMENT_ROUTES.failed}?session_id=${encodeURIComponent(sessionId)}`,
              { replace: true }
            );
            return;
          }
          setState({
            phase: "failed",
            message:
              payload.message ||
              "Your payment was not completed. You can try again when ready.",
            projectId,
            amount,
          });
          return;
        }

        if (isPaidInvestmentStatus(payload.status)) {
          setState({
            phase: "success",
            message:
              payload.message ||
              "Thank you! Your investment has been recorded and will appear in your dashboard shortly.",
            projectId,
            amount,
            investmentId: payload.investmentId,
          });
          return;
        }

        setState({
          phase: "processing",
          message:
            payload.message ||
            "Your payment is being processed. Check your dashboard shortly for an update.",
          projectId,
          amount,
        });
      } catch (err) {
        setState({
          phase: "error",
          message:
            err?.message ||
            "We could not verify your payment. If you were charged, contact support with your receipt.",
          projectId: pending?.projectId ?? null,
          amount: pending?.amount ?? null,
        });
      }
    })();
  }, [searchParams, navigate, redirectOnFailure]);

  return state;
}
